import { configureStore } from '@reduxjs/toolkit';
import { setAutoFreeze } from 'immer';
import { throttle } from 'lodash-es';
import createSagaMiddleware from 'redux-saga';
import type { SourceActionType } from 'src/commons/utils/ActionsHelper';

import {
  ALL_LANGUAGES,
  defaultLanguageConfig,
  defaultState,
  type OverallState,
} from '../commons/application/ApplicationTypes';
import rootReducer from '../commons/application/reducers/RootReducer';
import MainSaga from '../commons/sagas/MainSaga';
import { generateOctokitInstance } from '../commons/utils/GitHubPersistenceHelper';
import { loadStoredState, type SavedState, saveState } from './localStorage';

// FIXME: Hotfix: Disable auto freezing of states for RTK as this breaks the code evaluation sagas
setAutoFreeze(false);

export const store = createStore();

export function createStore() {
  const sagaMiddleware = createSagaMiddleware();
  const middleware = [sagaMiddleware];
  const initialStore = loadStore(loadStoredState()) || defaultState;

  const createdStore = configureStore<OverallState, SourceActionType>({
    reducer: rootReducer,
    // Fix for redux-saga type incompatibility
    // See: https://github.com/reduxjs/redux-toolkit/issues/3950
    middleware: middleware as any,
    devTools: { serialize: true, maxAge: 300 },
    // We already provide the generic type argument, so we put
    // `as any` to prevent excessively long type inference
    preloadedState: initialStore as any,
    // TODO: Sentry Redux integration once we update to RTK2
  });
  sagaMiddleware.run(MainSaga);

  createdStore.subscribe(
    throttle(() => {
      saveState(createdStore.getState());
    }, 1000),
  );

  return createdStore;
}

function loadStore(loadedStore: SavedState | undefined) {
  if (!loadedStore) {
    return undefined;
  }
  return {
    ...defaultState,
    session: {
      ...defaultState.session,
      ...(loadedStore.session ? loadedStore.session : {}),
      githubOctokitObject: {
        octokit: loadedStore.session.githubAccessToken
          ? generateOctokitInstance(loadedStore.session.githubAccessToken)
          : undefined,
      },
    },
    featureFlags: {
      modifiedFlags: {
        ...defaultState.featureFlags.modifiedFlags,
        ...loadedStore.featureFlags,
      },
    },
    workspaces: {
      ...defaultState.workspaces,
      playground: {
        ...defaultState.workspaces.playground,
        isFolderModeEnabled: loadedStore.playgroundIsFolderModeEnabled
          ? loadedStore.playgroundIsFolderModeEnabled
          : defaultState.workspaces.playground.isFolderModeEnabled,
        activeEditorTabIndex: loadedStore.playgroundActiveEditorTabIndex
          ? loadedStore.playgroundActiveEditorTabIndex.value
          : defaultState.workspaces.playground.activeEditorTabIndex,
        editorTabs: loadedStore.playgroundEditorTabs
          ? loadedStore.playgroundEditorTabs
          : defaultState.workspaces.playground.editorTabs,
        externalLibrary: loadedStore.playgroundExternalLibrary
          ? loadedStore.playgroundExternalLibrary
          : defaultState.workspaces.playground.externalLibrary,
        context: (() => {
          const chapter = loadedStore.playgroundSourceChapter
            ? loadedStore.playgroundSourceChapter
            : defaultState.workspaces.playground.context.chapter;
          const variant = loadedStore.playgroundSourceVariant
            ? loadedStore.playgroundSourceVariant
            : defaultState.workspaces.playground.context.variant;
          const validCombination = ALL_LANGUAGES.some(
            lang => lang.chapter === chapter && lang.variant === variant,
          );
          // Invalid stored combinations (e.g. chapter 1 + explicit-control, which can be
          // persisted when toggling feature flags) would crash on load, so fall back to
          // the default language config as a coherent, always-valid pair.
          return {
            ...defaultState.workspaces.playground.context,
            chapter: validCombination ? chapter : defaultLanguageConfig.chapter,
            variant: validCombination ? variant : defaultLanguageConfig.variant,
          };
        })(),
      },
    },
  };
}
